import { getSignalCategory, Signal, SignalCategory } from "./signalTypes";

export type SortKey = "created" | "category" | "status";
export type SignalView = "open" | "closed";

export interface SignalFilterState {
    view: SignalView; // Open vs Closed (Closed is read-only)
    categories: SignalCategory[]; // multi-select; empty = all
    cell?: string | null;
    pallet?: string | null;
    search: string;
    sort: SortKey;
}

export const DEFAULT_FILTER_STATE: SignalFilterState = {
    view: "open",
    categories: [],
    cell: null,
    pallet: null,
    search: "",
    sort: "created",
};

const CLOSED_STATUSES = new Set(["resolved", "rejected"]);

export const isClosed = (signal: Signal): boolean => CLOSED_STATUSES.has(signal.status);

export interface CategoryCounts {
    alert: number;
    action: number;
    advise: number;
    total: number;
}

/** Counts of OPEN signals per category (for the room-detail summary banner + tab badge). */
export const openCountsByCategory = (signals: Signal[]): CategoryCounts => {
    const counts: CategoryCounts = { alert: 0, action: 0, advise: 0, total: 0 };
    signals.forEach((signal) => {
        if (signal.status !== "open") return;
        const category = getSignalCategory(signal);
        if (category === "alert" || category === "action" || category === "advise") {
            counts[category] += 1;
            counts.total += 1;
        }
    });
    return counts;
};

const matchesSearch = (signal: Signal, term: string): boolean => {
    if (!term) return true;
    const needle = term.toLowerCase();
    const haystack = [
        signal.title,
        signal.description,
        signal.signal_type,
        ...(signal.relations || []).flatMap((r) => [r.facility_location_label, r.layer_label]),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(needle);
};

const CATEGORY_RANK: Record<string, number> = { alert: 0, action: 1, advise: 2 };
const STATUS_RANK: Record<string, number> = { open: 0, resolved: 1, rejected: 2 };

/** Pure filter + sort used by the worklist (and unit-tested directly). */
export const filterAndSortSignals = (signals: Signal[], state: SignalFilterState): Signal[] => {
    const filtered = signals.filter((signal) => {
        if (state.view === "open" && isClosed(signal)) return false;
        if (state.view === "closed" && !isClosed(signal)) return false;
        if (state.categories.length && !state.categories.includes(getSignalCategory(signal) as SignalCategory)) {
            return false;
        }
        if (state.cell && !(signal.relations || []).some((r) => r.facility_location_id === state.cell)) return false;
        if (state.pallet && !(signal.relations || []).some((r) => r.layer_id === state.pallet)) return false;
        if (!matchesSearch(signal, state.search)) return false;
        return true;
    });

    return [...filtered].sort((a, b) => {
        if (state.sort === "category") {
            return (CATEGORY_RANK[getSignalCategory(a) || ""] ?? 9) - (CATEGORY_RANK[getSignalCategory(b) || ""] ?? 9);
        }
        if (state.sort === "status") {
            return (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
        }
        // created: newest first
        return new Date(b.created).getTime() - new Date(a.created).getTime();
    });
};
